'use client';

import { MouseEventHandler, PropsWithChildren, useState } from 'react';

import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';

import { FormatPercent } from '@/blocks/formatters';
import { getCfdCategorySlugs, getFees } from '@/data';
import buttons from '@/styles/button.module.scss';
import context from '@/styles/contexts.module.scss';
import typography from '@/styles/typography.module.scss';
import { transformSharesToStocks } from '@/utils/string/map';

import styles from './styles.module.scss';

interface FeesCardProps {
  fees: Awaited<ReturnType<typeof getFees>>;
}

function Field({ i18nKey, children }: { i18nKey: string } & PropsWithChildren) {
  const tr = useTranslations();
  return (
    <div>
      {tr.rich(i18nKey, {
        value: () => <dd className={classNames(typography.bodyM)}>{children}</dd>,
        label: (label) => <dt className={classNames(typography.bodyS)}>{label}</dt>,
      })}
    </div>
  );
}

function Fields({ children }: PropsWithChildren) {
  return (
    <div className={styles.feesCardTradingConditions}>
      <dl>{children}</dl>
    </div>
  );
}

function Layout({ children }: PropsWithChildren) {
  return (
    <div className={classNames(styles.feesCard, context.light)}>
      <div className={styles.feesCardContent}>{children}</div>
    </div>
  );
}

function TabsContainer({ children }: PropsWithChildren) {
  return <div className={styles.feesCardCategoryTabs}>{children}</div>;
}

function Tab({
  children,
  active,
  onClick,
}: PropsWithChildren & { active?: boolean; onClick: MouseEventHandler }) {
  return (
    <button
      className={classNames(buttons.btn, buttons.btnPill, {
        [buttons.btnPillActive]: active,
      })}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function MainFieldValue({ children }: PropsWithChildren) {
  return <div className={styles.feesCardTradingFeePct}>{children}</div>;
}

function MainFieldLabel({ children }: PropsWithChildren) {
  return (
    <div className={classNames(styles.feesCardTradingFeeDescription, typography.subheadingM)}>{children}</div>
  );
}

function MainField({ children }: PropsWithChildren) {
  return (
    <div className={styles.feesCardTradingFeeContainer}>
      <div className={classNames(styles.feesCardTradingFeeContent)}>{children}</div>
    </div>
  );
}

export function FeesCard(props: FeesCardProps) {
  const categories = getCfdCategorySlugs();
  const { category } = useParams();

  const [selectedCategory, setSelectedCategory] = useState<string>((category as string) || categories[0]);
  const tr = useTranslations();
  const locale = useLocale();

  const tabs = categories.map((category) => ({
    value: category,
    label: tr.rich(`pages.cfd.index.sections.fees.tabs.${transformSharesToStocks(category)}`),
  }));

  const selectedFeesCategory = props.fees[selectedCategory];

  return (
    <Layout>
      <TabsContainer>
        {tabs.map(({ value, label }) => (
          <Tab key={value} active={value === selectedCategory} onClick={() => setSelectedCategory(value)}>
            {label}
          </Tab>
        ))}
      </TabsContainer>
      <MainField>
        {tr.rich('pages.cfd.index.sections.fees.fields.tradingFee', {
          value: () => (
            <MainFieldValue>
              <FormatPercent value={selectedFeesCategory.trading_fee} fraction={{ min: 2 }} />
            </MainFieldValue>
          ),
          label: (content) => <MainFieldLabel>{content}</MainFieldLabel>,
        })}
      </MainField>
      <Fields>
        {/*<Field i18nKey={'pages.cfd.index.sections.fees.fields.maxLeverage'}>*/}
        {/*  {selectedFeesCategory.max_leverage}*/}
        {/*</Field>*/}
        <Field i18nKey={'pages.cfd.index.sections.fees.fields.minimumDeposit'}>
          {tr.rich(
            `pages.cfd.index.sections.fees.tabs.minimumDeposit.${transformSharesToStocks(selectedFeesCategory.name)}`,
          )}
        </Field>
        <Field i18nKey={'pages.cfd.index.sections.fees.fields.orderTypes'}>
          {[
            tr('pages.cfd.index.sections.fees.tabs.limit'),
            tr('pages.cfd.index.sections.fees.tabs.stop'),
            tr('pages.cfd.index.sections.fees.tabs.market'),
          ].join(', ')}
        </Field>
        {/*<Field i18nKey={'pages.cfd.index.sections.fees.fields.numberOfAssets'}>*/}
        {/*  {selectedFeesCategory.assets}*/}
        {/*</Field>*/}
      </Fields>
    </Layout>
  );
}
